import { showEvents } from "@/Configs/config";
import { useTerminalsState } from "@/States/Terminals";
import { isAdmin } from "@/Utils/authUtils";
import { trpc } from "@/Utils/trpc";
import { useConfig } from "@/api/hooks/useConfig";
import {
  CustomerList,
  useCustomers,
} from "@/api/hooks/useCustomers/useCustomers";
import { useTrans } from "@/translations";

import { useCallback, useMemo } from "react";
import Skeleton from "react-loading-skeleton";

import { EmailJobType } from "server/config-api/index.js";

import { AccessManagementCard } from "./AccessManagementCard";
import { CustomerAdminCard } from "./AdminCard";
import { CustomerCommunicationCard } from "./CommunicationCard";
import { ExportDataCard } from "./ExportDataCard";
import { GeneralCustomerInfoCard } from "./GeneralInfoCard";
import { CustomerHistoryCard } from "./HistoryCard";
import { InfrastructureCard } from "./InfrastructureCard";
import { PeopleCard } from "./PeopleCard";
import { CustomerPrefilteringCard } from "./PreFilteringCard";

type SingleCustomerViewProps = {
  customerId: string;
  selectedCustomers: CustomerList;
};

export const SingleCustomerView = ({
  customerId,
  selectedCustomers,
}: SingleCustomerViewProps) => {
  const { isRealEstate, isMWM } = useConfig();
  const {
    data: customerWithCode,
    isError,
    isLoading,
    refetch,
  } = trpc.customers.get.useQuery({ id: customerId });
  const { t } = useTrans();
  const { refetchCustomers } = useCustomers();
  const { currentTerminal, allTerminals } = useTerminalsState();

  const {
    data: tenantActionReportJobs,
    isLoading: isLoadingTenantActionReportJobs,
    refetch: refetchTenantActionReportJobs,
  } = trpc.config.getTerminalEmailJobsByType.useQuery(
    {
      terminalId: currentTerminal?.id!,
      type: EmailJobType.TenantActionReport,
      tenantId: customerId,
    },
    {
      enabled: !!currentTerminal?.id && !!customerId && isRealEstate,
    },
  );

  const mwmTerminal = useMemo(
    () =>
      allTerminals.find((t) =>
        t.children?.some((c) =>
          customerWithCode?.accessParents.map((ap) => ap.id).includes(c.id),
        ),
      ),
    [allTerminals, customerWithCode],
  );

  const {
    data: householdReportJobs,
    isLoading: isLoadingHouseholdReportJobs,
    refetch: refetchHouseholdReportJobs,
  } = trpc.config.getTerminalEmailJobsByType.useQuery(
    {
      terminalId: mwmTerminal?.id!,
      type: EmailJobType.ReportHouseholdCustomers,
      tenantId: customerId,
    },
    {
      enabled: !!mwmTerminal?.id && !!customerId && isMWM,
    },
  );

  const onUpdateSingleCustomer: () => Promise<any> = useCallback(async () => {
    await refetch();

    if (isMWM) {
      await refetchHouseholdReportJobs();
    } else {
      await refetchTenantActionReportJobs();
    }
  }, [
    refetch,
    refetchTenantActionReportJobs,
    refetchHouseholdReportJobs,
    isMWM,
  ]);

  const onUpdate: () => Promise<any> = useCallback(async () => {
    await refetchCustomers();
    await onUpdateSingleCustomer();
  }, [refetchCustomers, onUpdateSingleCustomer]);

  const reportJobs = useMemo(
    () => (isMWM ? householdReportJobs : tenantActionReportJobs) || [],
    [isMWM, householdReportJobs, tenantActionReportJobs],
  );

  const terminalId = useMemo(
    () => (isMWM ? mwmTerminal?.id : currentTerminal?.id),
    [isMWM, mwmTerminal, currentTerminal],
  );

  const customer = customerWithCode;

  const showContactsCard = useMemo(() => {
    if (!customer) return false;

    if (isMWM) return isAdmin();

    return true;
  }, [customer, isMWM]);

  if (
    isLoading ||
    (isRealEstate && isLoadingTenantActionReportJobs) ||
    (isMWM && isLoadingHouseholdReportJobs)
  )
    return <Skeleton height={300} count={3} />;

  if (isError || !customer) return <>{t("errors:failedToLoadCustomer")}</>;

  return (
    <>
      <h4 className="font-signifier text-3xl font-light text-black">
        {customer.name}
      </h4>
      <GeneralCustomerInfoCard customer={customer} onUpdate={onUpdate} />
      {(isRealEstate || showEvents) && (
        <CustomerPrefilteringCard customerId={customerId} />
      )}
      {showContactsCard && (
        <PeopleCard
          customer={customer}
          onUpdate={onUpdateSingleCustomer}
          reportJobs={reportJobs}
          terminalId={terminalId}
        />
      )}
      {(!!customer?.accessParents?.length || isMWM) && (
        <InfrastructureCard customer={customer} refetch={onUpdate} />
      )}
      {customer && (
        <AccessManagementCard customer={customer} refetch={onUpdate} />
      )}
      {isRealEstate && customer && (
        <CustomerCommunicationCard customer={customer} />
      )}
      <CustomerHistoryCard customerId={customerId} />
      <ExportDataCard selectedCustomers={selectedCustomers} />
      <CustomerAdminCard
        customerId={customerId}
        customerName={customer?.name}
      />
    </>
  );
};
